import applyCaseMiddleware from "axios-case-converter"; // TODO: かなりニッチなライブラリのため、自前で実装するか他のライブラリを探す
import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosResponse,
} from "axios";
import { useNavigate } from "react-router-dom";
import { useCookies, Cookies } from "react-cookie";
import { type IJwtResponse } from "../types/jwt";
import { type ICookiesType } from "../types/cookies";
import { baseURL } from "../configs";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { User } from "@/types/localstorage";

interface IUseApiClientReturnType {
  apiClient: AxiosInstance;
}

// JWTリフレッシュAPIのパス
const REFRESH_TOKEN_ENDPOINT = "auth/jwt/refresh";

const useApiClient = (): IUseApiClientReturnType => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const navigate = useNavigate();
  const [, , removeValue] = useLocalStorage<User | undefined>(
    "user",
    undefined
  );

  const apiClient: AxiosInstance = applyCaseMiddleware(
    axios.create({
      baseURL,
    })
  );

  apiClient.interceptors.request.use((config) => {
    const c = new Cookies();
    const accesstoken = c.get<string>("accesstoken");
    config.headers.Authorization = `JWT ${accesstoken}`;
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      // 無限ループ防止：リフレッシュリクエストでのエラーは除外
      if (error.config?.url === REFRESH_TOKEN_ENDPOINT) {
        // リレッシュトークンが期限切れの場合はログイン画面に遷移
        // uselogin.tsのlogout()と同等、useLogin()はこのファイル内で利用できなかった（テストが通らない）
        removeCookie("accesstoken", { path: "/" });
        removeCookie("refreshtoken", { path: "/" });
        localStorage.removeItem("mapServiceAppId");
        removeValue();
        setCookie("isLogin", "false", { path: "/" });
        navigate("/login");
        return await Promise.reject(error);
      }

      // API通信時にトークン切れになった場合、トークンを更新する
      if (error.response?.config != null && error.response.status === 401) {
        const { refreshtoken } = cookies as ICookiesType;

        // refreshtokenがundefinedの場合は、どうやってもアクセストークンは得られないので"auth/jwt/refresh"の呼び出しは諦める
        if (refreshtoken === undefined) {
          return await Promise.reject(error);
        }

        await apiClient
          .post(REFRESH_TOKEN_ENDPOINT, {
            // ここが例外になった場合catch節(現在は削除済み)に行ってほしいが、実際は43行目が新規で呼び出されてしまう
            refresh: refreshtoken,
          })
          .then((res: AxiosResponse<IJwtResponse>) => {
            setCookie("accesstoken", res.data.access, { path: "/" });
            setCookie("refreshtoken", res.data.refresh, { path: "/" });
          });
      }
      return await Promise.reject(error);
    }
  );

  return { apiClient };
};

export { useApiClient };
